import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Vehicle } from 'rest-client';

import { VehicleService } from '@app/modules/core/services/vehicle/vehicle.service';
import {
  BodyResponse,
  MotorBodyResponse,
  MotorResponse,
  RequestedVehicleDataResponse,
  RequestedVehicleDataPayload,
  VehicleData,
} from '@app/modules/shared/models';
import { RequestedCarServicesData } from '@app/modules/shared/utils/vehicle-status-checker';
import { isToday } from '@shared/utils/vehicle-status-checker';

interface ExtendedMotorBodyResponse extends MotorBodyResponse {
  friendlyName?: string;
}

/**
 * > Shows custom selectors when Car Service Family requires more data.
 */
@UntilDestroy()
@Component({
  selector: 'bkg-vehicle-dialog-requested-data',
  templateUrl: './vehicle-dialog-requested-data.component.html',
})
export class VehicleDialogRequestedDataComponent implements OnInit {
  @Input() vehicleData: VehicleData;
  @Input() requestedCSData: RequestedCarServicesData;
  @Input() isLogged: boolean;
  @Input() moreInfoShowing: boolean;
  @Input() clientId: number;
  @Input() modifiedVehicle: Vehicle;
  @Output() errorReturn = new EventEmitter<HttpErrorResponse>();
  @Output() allRequestedReturn = new EventEmitter<Vehicle | null>();

  requestedDataForm: FormGroup;

  showKmInput: boolean;
  bodyOptions = [] as MotorBodyResponse[];
  motorOptions = [] as ExtendedMotorBodyResponse[];
  loadingMotor: boolean;

  constructor(private vehicleService: VehicleService) {}

  ngOnInit(): void {
    if (this.checkRequestedVehicleData()) {
      this.allRequestedReturn.emit();
    } else {
      this.initDataFlow();
    }
  }

  private initDataFlow(): void {
    this.buildRequestedDataForm();
  }

  private checkRequestedVehicleData = (): boolean => {
    if (
      (!this.vehicleData.km && this.requestedCSData.km) ||
      (!isToday(this.vehicleData.kmTimestamp) && this.requestedCSData.km)
    ) {
      return false;
    }
    if (this.requestedCSData.bodyCode && !this.vehicleData.bodyCode) {
      return false;
    }
    if (this.requestedCSData.motorCode && !this.vehicleData.motorCode) {
      return false;
    }
    return true;
  };

  private buildRequestedDataForm = (): void => {
    const showKmInput =
      (!this.vehicleData.km && this.requestedCSData.km) ||
      (!isToday(this.vehicleData.kmTimestamp) && this.requestedCSData.km) ||
      this.modifiedVehicle?.id;
    const group = {} as Record<string, FormControl>;

    if (!this.vehicleData.bodyCode && this.requestedCSData.bodyCode) {
      this.getBodyOptions();
      group['body_applicabilitycode'] = new FormControl('', [
        Validators.required,
      ]);
      group['body_applicabilitycode'].setValue(null);
    }

    if (!this.vehicleData.motorCode && this.requestedCSData.motorCode) {
        this.getMotorOptions();
      group['motor_applicabilitycode'] = new FormControl('', [
        Validators.required,
      ]);
      group['motor_applicabilitycode'].setValue(null);
    }

    if (showKmInput) {
      group['km'] = new FormControl('', [Validators.required]);
      if (this.vehicleData.km) {
        group['km'].setValue(this.vehicleData.km);
      }
    }

    this.requestedDataForm = new FormGroup(group);
  };

  private getBodyOptions = (): void => {
    this.vehicleService
      .getBodyOptions(this.vehicleData.id, this.clientId)
      .pipe(untilDestroyed(this))
      .subscribe(
        (response: BodyResponse) => (this.bodyOptions = response?.body_options),
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };

  private getMotorOptions = (): void => {
    this.loadingMotor = true;
    this.vehicleService
      .getMotorOptions(this.vehicleData.id, this.clientId)
      .pipe(untilDestroyed(this))
      .subscribe(
        (response: MotorResponse) => {
          this.motorOptions = !!response?.motor_options
            ? response.motor_options
            : [];
          this.motorOptions.forEach((motor: ExtendedMotorBodyResponse) => {
            const splitMotor = motor.desc.split(/[CC()]+/);
            const roundedCc = Math.round(Number(splitMotor[0]) / 100) * 100;
            motor.friendlyName =
              roundedCc.toString() + ' cc - ' + splitMotor[2] + 'CV';
          });
          this.motorOptions = this.motorOptions?.filter(
            (
              v: ExtendedMotorBodyResponse,
              i: number,
              a: ExtendedMotorBodyResponse[]
            ) =>
              a.findIndex(
                (t: ExtendedMotorBodyResponse) =>
                  t.friendlyName === v.friendlyName
              ) === i
          );
          this.loadingMotor = false;
        },
        (error: HttpErrorResponse) => {
          this.errorReturn.emit(error);
          this.loadingMotor = false;
        }
      );
  };

  get motorControl(): AbstractControl | null {
    return this.getControl('motor_applicabilitycode') || null;
  }

  get bodyControl(): AbstractControl | null {
    return this.getControl('body_applicabilitycode') || null;
  }

  get kmControl(): AbstractControl | null {
    return this.getControl('km') || null;
  }

  private getControl(controlName: string): AbstractControl {
    return this.requestedDataForm.get(controlName);
  }

  submitData = (): void => {
    const id = Number(this.vehicleData.id);
    const requestedDataPayload = this.createPayload();
    if (this.isLogged) {
      this.sendRequestData(id, requestedDataPayload);
    } else {
      this.sendRequestDataGuest(id, requestedDataPayload);
    }
  };

  private createPayload = (): RequestedVehicleDataPayload => {
    const payload: RequestedVehicleDataPayload = {};

    if (this.motorControl) {
      const motorOption = this.getmotorOptionfromModelDescription(
        this.getControlValue('motor_applicabilitycode') as string
      );
      payload.motor_applicabilitycode = motorOption
        ? motorOption.applicabilitycode
        : null;
      payload.motor_desc = motorOption ? motorOption.desc : null;
    }

    if (this.bodyControl) {
      const bodySelected = this.getControlValue(
        'body_applicabilitycode'
      ) as string;
      const bodyOption = this.bodyOptions.find(
        (body: MotorBodyResponse) => body.applicabilitycode === bodySelected
      );
      payload.body_applicabilitycode = bodyOption.applicabilitycode;
      payload.body_desc = bodyOption.desc;
    }

    if (this.kmControl) {
      let kmParsed = this.getControlValue('km').toString();
      kmParsed = kmParsed.replace(/\./g, '');
      payload.km = Number(kmParsed);
    }

    return payload;
  };

  private getmotorOptionfromModelDescription(
    applicabilityCode: string
  ): MotorBodyResponse {
    return this.motorOptions.find(
      (motor: MotorBodyResponse) =>
        motor.applicabilitycode === applicabilityCode
    );
  }

  private getControlValue(controlName: string): string | number {
    return this.getControl(controlName).value as string | number;
  }

  private sendRequestData = (
    id: number,
    payload: RequestedVehicleDataPayload
  ): void => {
    this.vehicleService
      .setRequestedVehicleData(id, payload)
      .pipe(untilDestroyed(this))
      .subscribe(
        (response: unknown) => {
          const { vehicle } = response as RequestedVehicleDataResponse;
          this.allRequestedReturn.emit(vehicle);
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };

  private sendRequestDataGuest = (
    id: number,
    payload: RequestedVehicleDataPayload
  ): void => {
    this.vehicleService
      .setRequestedVehicleDataGuest(id, payload)
      .pipe(untilDestroyed(this))
      .subscribe(
        (response: unknown) => {
          const { vehicle } = response as RequestedVehicleDataResponse;
          this.allRequestedReturn.emit(vehicle);
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };
}
